import Image from 'next/image';
import { RichText } from 'prismic-reactjs';
import { fluidProp } from 'remote-com-shared/style/helpers';
import { AnimatedSection, sharedTransition } from 'remote-com-shared/style/motion';
import styled from 'styled-components';
import { SwiperSlide } from 'swiper/react';

import {
  WhyRemoteLabel,
  TitleComponent as Title,
  DescriptionComponent,
} from '@/src/components/WhyRemote';
import { AnimatedSectionWrapper } from '@/src/style/motion';
import { CountryCardsSwiper, CardsList } from '@/src/views/RemoteRelocation/WorkAnywhere';

export const TitleComponent = styled(Title)`
  max-width: 775px;
`;

export const CardEntry = styled.div`
  box-sizing: border-box;
  padding: 31px 29px 31px 30px;
  background-color: var(--colors-blank);
  width: 330px;
  height: 395px;
  box-shadow: 1px 8px 45px rgba(0, 35, 75, 0.09);
  border-radius: 24px;
  white-space: normal;
  vertical-align: top;
  display: inline-block;
  opacity: 0.5;

  &:hover {
    opacity: 1;
  }
`;

export const SectionWrapper = styled.section`
  ${fluidProp(['padding-top', 'padding-bottom'], '80px', '130px')}
  background-image: var(--colors-zircon);
  overflow: hidden;

  .swiper-pagination {
    position: absolute;
    bottom: 0;
    display: flex;
    justify-content: center;
    left: 0;
    right: 0;

    .swiper-pagination-bullet {
      width: 10px;
      height: 6px;
      background-color: var(--colors-irisBlue);
      opacity: 0.2;
      border-radius: 6px;
      cursor: pointer;
      margin: 0 3px;
      ${sharedTransition('width, opacity')}
    }

    .swiper-pagination-bullet-active {
      opacity: 1;
      width: 34px;
    }
  }
  .swiper-slide-active {
    ${CardEntry} {
      opacity: 1;
    }
  }

  .swiper-container {
    padding-top: 0;
    padding-bottom: 112px;
  }
`;

export const ImageWrapper = styled.div`
  background-image: var(--colors-zircon);
  border-radius: 18.75px;
  width: 76px;
  height: 76px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export const CardTitle = styled.div`
  ${({ theme }) => theme.typography.bodyLeadBold}
  font-weight: 600;

  ${ImageWrapper} + & {
    margin-top: 23px;
  }

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: 0;
  }
`;

export const CardDescription = styled.div`
  ${({ theme }) => theme.typography.body}
  color: var(--colors-bayoux);
  margin-top: 14px;

  > * {
    font-size: inherit;
    font-weight: inherit;
    margin: 0;
  }
`;

export const Wrapper = styled.div`
  display: flex;
  justify-content: center;
  overflow-x: auto;
  white-space: nowrap;
  padding: 30px 0;
  display: block;
`;

export function Entry({ title, illustration, description }) {
  return (
    <AnimatedSection ComponentName={CardEntry}>
      {illustration && (
        <ImageWrapper>
          <Image
            src={illustration.url}
            alt={illustration.alt || ''}
            width="30"
            height="30"
            quality="100"
          />
        </ImageWrapper>
      )}
      {title && <RichText render={title} Component={CardTitle} />}
      {description && <RichText render={description} Component={CardDescription} />}
    </AnimatedSection>
  );
}

export function HowWeHelpSection({ label, title, description, list = [], ...props }) {
  return (
    <SectionWrapper {...props}>
      <AnimatedSection ComponentName={AnimatedSectionWrapper}>
        {label && (
          <WhyRemoteLabel>
            <small>{label}</small>
          </WhyRemoteLabel>
        )}
        {title && <RichText render={title} Component={TitleComponent} />}
        {description && <RichText render={description} Component={DescriptionComponent} />}
      </AnimatedSection>

      <Wrapper>
        <AnimatedSection
          ComponentName={CountryCardsSwiper}
          centeredSlides
          grabCursor
          a11y
          keyboard={{
            enabled: true,
            onlyInViewport: true,
          }}
          slidesPerView="auto"
          spaceBetween={24}
          pagination={{
            clickable: true,
          }}
          initialSlide={1}
        >
          <CardsList>
            {list.map((listItem, index) => (
              <SwiperSlide key={index}>
                <Entry {...listItem} />
              </SwiperSlide>
            ))}
          </CardsList>
        </AnimatedSection>
      </Wrapper>
    </SectionWrapper>
  );
}
